import type {
  ApiResponse,
  AdminBootstrapStatusResponse,
  AdminBootstrapRequest,
  AdminLoginRequest,
  AdminLoginResponse,
  AdminAccountDto,
  AdminStatsResponse,
  AdminUsersListResponse,
  AdminUserDetailResponse,
  AdminMinersListResponse,
  AdminMinerActionRequest,
  AdminSettingsDto,
  AdminUpdateSettingsRequest,
  AdminStarsOrdersResponse,
  AdminAdAttemptsResponse,
  AdminTaskAttemptsResponse,
  AdminReferralTransactionsResponse,
  AdminAuditLogsResponse,
} from '@nova-mine/shared';

const API_BASE = import.meta.env.VITE_ADMIN_API_BASE_URL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

let adminToken: string = localStorage.getItem('nova_admin_token') || '';
let adminApiKey: string = localStorage.getItem('nova_admin_api_key') || '';
let currentAdmin: AdminAccountDto | null = null;

try {
  const cachedAdmin = localStorage.getItem('nova_admin_profile');
  if (cachedAdmin) {
    currentAdmin = JSON.parse(cachedAdmin);
  }
} catch {
  // ignore
}

export function setAdminSession(token: string, admin: AdminAccountDto) {
  adminToken = token;
  currentAdmin = admin;
  localStorage.setItem('nova_admin_token', token);
  localStorage.setItem('nova_admin_profile', JSON.stringify(admin));
}

export function getAdminToken(): string {
  return adminToken;
}

export function getCurrentAdmin(): AdminAccountDto | null {
  return currentAdmin;
}

export function setAdminApiKey(key: string) {
  adminApiKey = key;
  localStorage.setItem('nova_admin_api_key', key);
}

export function getAdminApiKey(): string {
  return adminApiKey;
}

export function clearAdminAuth() {
  adminToken = '';
  adminApiKey = '';
  currentAdmin = null;
  localStorage.removeItem('nova_admin_token');
  localStorage.removeItem('nova_admin_profile');
  localStorage.removeItem('nova_admin_api_key');
}

export function isAuthenticated(): boolean {
  return Boolean(adminToken || adminApiKey);
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers || {});
  headers.set('Content-Type', 'application/json');

  if (adminToken) {
    headers.set('Authorization', `Bearer ${adminToken}`);
  } else if (adminApiKey) {
    headers.set('X-Admin-Key', adminApiKey);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  const json: ApiResponse<T> = await response.json().catch(() => ({
    success: false,
    error: `Invalid server response (${response.status})`,
  }));

  if (!response.ok || !json.success) {
    if (response.status === 401) {
      // Clear expired credentials
      clearAdminAuth();
    }
    throw new Error(json.error || `Request failed with status ${response.status}`);
  }

  return json.data as T;
}

export const adminApi = {
  // 1. Authentication & Bootstrap
  async getBootstrapStatus(): Promise<AdminBootstrapStatusResponse> {
    return request<AdminBootstrapStatusResponse>('/api/admin/auth/bootstrap-status');
  },

  async bootstrap(data: AdminBootstrapRequest): Promise<AdminLoginResponse> {
    const res = await request<AdminLoginResponse>('/api/admin/auth/bootstrap', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    setAdminSession(res.token, res.admin);
    return res;
  },

  async login(data: AdminLoginRequest): Promise<AdminLoginResponse> {
    const res = await request<AdminLoginResponse>('/api/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    setAdminSession(res.token, res.admin);
    return res;
  },

  async logout(): Promise<void> {
    try {
      await request('/api/admin/auth/logout', { method: 'POST' });
    } finally {
      clearAdminAuth();
    }
  },

  async getMe(): Promise<{ admin: AdminAccountDto }> {
    return request<{ admin: AdminAccountDto }>('/api/admin/auth/me');
  },

  // 2. Metrics & Telemetry
  async getStats(): Promise<AdminStatsResponse> {
    return request<AdminStatsResponse>('/api/admin/stats');
  },

  // 3. User Management
  async getUsers(
    limit = 25,
    offset = 0,
    q = '',
    status = 'all'
  ): Promise<AdminUsersListResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      status,
    });
    if (q) params.set('q', q);
    return request<AdminUsersListResponse>(`/api/admin/users?${params.toString()}`);
  },

  async getUserDetail(userId: string): Promise<AdminUserDetailResponse> {
    return request<AdminUserDetailResponse>(`/api/admin/users/${userId}`);
  },

  async adjustBalance(userId: string, amount: number, reason: string) {
    return request<{
      user_id: string;
      previous_balance: number;
      adjustment: number;
      new_balance: number;
      transaction_id: string;
    }>(`/api/admin/users/${userId}/adjust`, {
      method: 'POST',
      body: JSON.stringify({ amount, reason }),
    });
  },

  async toggleBan(userId: string, banned: boolean) {
    return request<{ user_id: string; is_banned: boolean }>(`/api/admin/users/${userId}/ban`, {
      method: 'POST',
      body: JSON.stringify({ banned }),
    });
  },

  // 4. Miner Management
  async getMiners(
    limit = 25,
    offset = 0,
    type = 'all',
    status = 'all',
    q = ''
  ): Promise<AdminMinersListResponse> {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
      type,
      status,
    });
    if (q) params.set('q', q);
    return request<AdminMinersListResponse>(`/api/admin/miners?${params.toString()}`);
  },

  async minerAction(minerId: string, action: 'pause' | 'resume' | 'terminate', reason: string) {
    return request<{ miner_id: string; action: string; status: string }>(
      `/api/admin/miners/${minerId}/action`,
      {
        method: 'POST',
        body: JSON.stringify({ action, reason }),
      }
    );
  },

  // 5. System Settings
  async getSettings(): Promise<AdminSettingsDto> {
    return request<AdminSettingsDto>('/api/admin/settings');
  },

  async updateSettings(settings: AdminUpdateSettingsRequest) {
    return request<{ success: boolean; message: string; data: Record<string, string> }>(
      '/api/admin/settings',
      {
        method: 'POST',
        body: JSON.stringify(settings),
      }
    );
  },

  // 6. Payment, Task & Activity Logs
  async getStarsOrders(limit = 50, status = 'all', q = ''): Promise<AdminStarsOrdersResponse> {
    const params = new URLSearchParams({ limit: String(limit), status });
    if (q) params.set('q', q);
    return request<AdminStarsOrdersResponse>(`/api/admin/stars-orders?${params.toString()}`);
  },

  async getAdAttempts(limit = 50, status = 'all'): Promise<AdminAdAttemptsResponse> {
    const params = new URLSearchParams({ limit: String(limit), status });
    return request<AdminAdAttemptsResponse>(`/api/admin/ad-attempts?${params.toString()}`);
  },

  async getTaskAttempts(limit = 50): Promise<AdminTaskAttemptsResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    return request<AdminTaskAttemptsResponse>(`/api/admin/task-attempts?${params.toString()}`);
  },

  async getReferralTransactions(limit = 50): Promise<AdminReferralTransactionsResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    return request<AdminReferralTransactionsResponse>(
      `/api/admin/referral-transactions?${params.toString()}`
    );
  },

  async getAuditLogs(limit = 50): Promise<AdminAuditLogsResponse> {
    const params = new URLSearchParams({ limit: String(limit) });
    return request<AdminAuditLogsResponse>(`/api/admin/audit-logs?${params.toString()}`);
  },
};
